import React, {useEffect, useRef, useState} from 'react';
import {ServiceResource, useAuth} from '../hooks/use-auth';
import {Notification} from '../navigation/page-layout';
import {useRetrieveInboundBridgeData} from '../hooks/use-analytics-api';
import {useInboundBridgeFilterData} from '../hooks/use-inbound-bridge-metadata';
import {translateErrorToReactNode} from '../common';
import {Box, ContentLayout, SpaceBetween, Spinner} from '@amzn/awsui-components-react';
import {BridgeType, InboundBridgeRecord} from './index';
import {InboundBridgeTable} from './inbound-bridge-table';
import {InboundBridgeFilters} from './inbound-bridge-filters';

export function InboundBridgeP2P(props: {pushNotification: (notification: Notification) => void}) {
    function createErrorListener<T>(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const auth = useAuth();
    const [bridgeData, setBridgeData] = useState<InboundBridgeRecord[]>([]);
    const isLoadingData = useRef<boolean>(true);

    const tenant = {
        business: auth.authInformation!.current!.businessId,
        country: auth.authInformation!.current!.country,
    };

    const clientConfiguration = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView);
    const {
        execute: executeRetrieveInboundBridgeData,
        status: inboundBridgeDataStatus,
        value: getInboundBridgeDataResponse,
    } = useRetrieveInboundBridgeData(clientConfiguration, createErrorListener('RetrieveInboundBridgeData failed'), [auth]);

    const {nodeFilterDataMap, selectedFilter, setSelectedFilter, isLoadingMetadata} = useInboundBridgeFilterData(
        BridgeType.P2P,
        tenant,
        clientConfiguration,
        createErrorListener,
        executeRetrieveInboundBridgeData
    );

    isLoadingData.current = ['idle', 'pending'].includes(inboundBridgeDataStatus);

    useEffect(() => {
        if (!isLoadingData.current && getInboundBridgeDataResponse?.records) {
            const newData: InboundBridgeRecord[] = getInboundBridgeDataResponse.records.map(
                (r) =>
                    ({
                        date: r.balanceDate.split('T')[0],
                        node: r.node,
                        granularity: r.granularity,
                        bucketSeqId: r.bucketSeqId,
                        bucketName: r.bucketName,
                        bucketValue: r.bucketValue,
                        bucketError: r.bucketError,
                        lastBucketValue: r.lastBucketValue,
                        wapeContribution: `${Math.round((Math.abs(r.bucketError) / r.lastBucketValue) * 100)}%`,
                    } as InboundBridgeRecord)
            );

            setBridgeData(newData);
        }
    }, [isLoadingData.current]);

    return (
        <ContentLayout defaultPadding>
            <SpaceBetween direction="vertical" size="xxl">
                <InboundBridgeFilters
                    bridgeType={BridgeType.P2P}
                    nodeFilterDataMap={nodeFilterDataMap}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    isLoadingMetadata={isLoadingMetadata}
                    isLoadingData={isLoadingData.current}
                    onApply={() => {
                        if (selectedFilter) {
                            isLoadingData.current = true;
                            executeRetrieveInboundBridgeData({
                                ...tenant,
                                forecastType: 'sop',
                                bridgeType: BridgeType.P2P,
                                filter: JSON.stringify({
                                    node: selectedFilter.node,
                                    balanceDateStart: selectedFilter.balanceDateStart,
                                    balanceDateEnd: selectedFilter.balanceDateEnd,
                                    fromHorizonInWeeks: selectedFilter.fromHorizonInWeeks,
                                    toHorizonInWeeks: selectedFilter.toHorizonInWeeks,
                                }),
                            });
                        }
                    }}
                />
                {renderVisualizations()}
            </SpaceBetween>
        </ContentLayout>
    );

    function renderVisualizations(): React.ReactElement {
        if (isLoadingMetadata || isLoadingData.current) {
            return (
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            );
        }

        return <InboundBridgeTable bridgeData={bridgeData} selectedFilter={selectedFilter} />;
    }
}
